/* eslint-disable import/no-unresolved */
import React from 'react';

import DropdownMC from '../../../components/DropdownMC';
import ButtonMC from '../../../components/ButtonMC';
import ToggleSwitchMC from '../../../components/ToggleSwitchMC';
import InputMC from '../../../components/InputMC';

/**
 * Shared UI: default message settings + optional fields_mapping to customer state.
 * Used by WhatsappFlowMappingModal and the MIA trigger_whatsapp_flow tool picker.
 */
const WhatsappFlowMessageAndMappingConfig = ({
  t,
  defaultHeaderText,
  setDefaultHeaderText,
  defaultBodyText,
  setDefaultBodyText,
  defaultFooterText,
  setDefaultFooterText,
  defaultFlowCta,
  setDefaultFlowCta,
  showCustomerMapping = true,
  saveToCustomerState,
  handleToggleSaveToCustomer,
  mappings,
  customerFieldsOptions,
  newFlowKey,
  setNewFlowKey,
  newCustomerField,
  setNewCustomerField,
  availableFlowKeysOptions,
  availableCustomerFieldsOptions,
  handleAddMapping,
  handleRemoveMapping
}) => (
  <div className="tw-space-y-6">
    <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-space-y-4">
      <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">
        {t('default_message_settings')}
      </h3>
      <div className="tw-space-y-4">
        <InputMC
          name="defaultHeaderText"
          label={t('default_header_text_label')}
          placeholder={t('default_header_text_placeholder')}
          value={defaultHeaderText}
          onChange={(name, val) => setDefaultHeaderText(val)}
          useFloatingLabel
        />
        <InputMC
          textarea
          rows={3}
          name="defaultBodyText"
          label={t('default_body_text_label')}
          placeholder={t('default_body_text_placeholder')}
          value={defaultBodyText}
          onChange={(name, val) => setDefaultBodyText(val)}
          useFloatingLabel
          className="!tw-placeholder-gray-400"
        />
        <InputMC
          name="defaultFooterText"
          label={t('default_footer_text_label')}
          placeholder={t('default_footer_text_placeholder')}
          value={defaultFooterText}
          onChange={(name, val) => setDefaultFooterText(val)}
          useFloatingLabel
        />
        <InputMC
          name="defaultFlowCta"
          label={t('default_flow_cta_label')}
          placeholder={t('default_flow_cta_placeholder')}
          value={defaultFlowCta}
          onChange={(name, val) => setDefaultFlowCta(val)}
          useFloatingLabel
        />
      </div>
    </div>

    {showCustomerMapping && (
      <>
        <div className="tw-bg-blue-50 tw-rounded-2xl tw-p-5 tw-border tw-border-blue-100 tw-flex tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-flex-1">
            <h4 className="tw-text-sm tw-font-bold tw-text-blue-900">
              {t('save_to_customer_state')}
            </h4>
            <p className="tw-text-xs tw-text-blue-700 tw-mt-1">
              {t('auto_mapping_description')}
            </p>
          </div>
          <div className="tw-flex-shrink-0">
            <ToggleSwitchMC
              isOn={saveToCustomerState}
              handleToggle={handleToggleSaveToCustomer}
              size="medium"
            />
          </div>
        </div>

        {saveToCustomerState && (
          <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-space-y-6">
            <div className="tw-space-y-4">
              <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">
                {t('select_fields_to_map')}
              </h3>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-items-end">
                <div>
                  <DropdownMC
                    key={`flow-key-${newFlowKey}`}
                    id="new-flow-key"
                    label={t('flow_key')}
                    options={availableFlowKeysOptions}
                    defaultValue={newFlowKey}
                    onSelect={(val) => setNewFlowKey(val)}
                    placeholder={t('select_field_placeholder')}
                    buttonClassName="!tw-h-[48px]"
                    useFloatingLabel
                  />
                </div>
                <div>
                  <DropdownMC
                    key={`customer-field-${newCustomerField}`}
                    id="new-customer-field"
                    label={t('customer_field')}
                    options={availableCustomerFieldsOptions}
                    defaultValue={newCustomerField}
                    onSelect={(val) => setNewCustomerField(val)}
                    placeholder={t('select_field_placeholder')}
                    buttonClassName="!tw-h-[48px]"
                    useFloatingLabel
                  />
                </div>
                <div>
                  <ButtonMC
                    text={t('add')}
                    onClick={handleAddMapping}
                    variant="PRIMARY"
                    className="tw-w-full !tw-h-[48px]"
                    disabled={!newFlowKey || !newCustomerField}
                  />
                </div>
              </div>
            </div>

            <div className="tw-border-t tw-border-gray-150 tw-pt-5 tw-space-y-3">
              <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">
                {t('mapped_fields')}
              </h3>

              {mappings.length === 0 ? (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-8 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200">
                  <p className="tw-text-sm tw-text-gray-400 tw-italic">
                    {t('no_mapped_fields')}
                  </p>
                </div>
              ) : (
                <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden">
                  <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                    <thead className="tw-bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider"
                        >
                          {t('flow_key')}
                        </th>
                        <th
                          scope="col"
                          className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider"
                        >
                          {t('customer_field')}
                        </th>
                        <th scope="col" className="tw-relative tw-px-4 tw-py-3 tw-w-16">
                          <span className="tw-sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                      {mappings.map((mapping) => (
                        <tr
                          key={mapping.flowKey}
                          className="hover:tw-bg-gray-50 tw-transition-colors"
                        >
                          <td className="tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-gray-700 tw-whitespace-nowrap">
                            {mapping.flowKey}
                          </td>
                          <td className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-whitespace-nowrap">
                            {customerFieldsOptions.find((o) => o.id === mapping.customerField)?.label
                              || (mapping.customerField === 'customer_documents' ? t('fields.customer_documents') : mapping.customerField)}
                          </td>
                          <td className="tw-px-4 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-sm">
                            <ButtonMC
                              onClick={() => handleRemoveMapping(mapping.flowKey)}
                              variant="GRAY"
                              outlined
                              className="tw-p-2 !tw-border-gray-200 hover:!tw-border-red-500 hover:!tw-text-red-500 hover:!tw-bg-red-50 tw-text-gray-400"
                              title={t('delete_mapping')}
                              icon="trash-2-outline"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    )}
  </div>
);

export default WhatsappFlowMessageAndMappingConfig;
